"use client";

import { Pause, Play, RotateCcw } from "lucide-react";
import { startTransition, useEffect, useMemo, useRef, useState } from "react";
import {
  useIsMarketingMobile,
  useResolvedMarketingTheme,
} from "@/hooks/useNcMedia";
import {
  buildHowtoGifCandidates,
  buildHowtoPosterCandidates,
  buildHowtoWebmCandidates,
  gettingAroundById,
  type GettingAroundId,
} from "@/lib/onboarding/gettingAroundCatalog";
import { cn } from "@/lib/utils";
import { useMarketingText } from "@/lib/i18n/marketingClient";
import type { MarketingLocale } from "@/lib/i18n/marketingLocale";

type Speed = 0.5 | 0.75 | 1;

export function HowToGifClient({
  entryId,
  className,
  priority = false,
}: {
  entryId: GettingAroundId;
  className?: string;
  priority?: boolean;
  locale: MarketingLocale;
}) {
  const entry = gettingAroundById(entryId);
  const localize = useMarketingText();
  const theme = useResolvedMarketingTheme();
  const isMobile = useIsMarketingMobile();
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const gifCandidates = useMemo(
    () => buildHowtoGifCandidates(isMobile, theme, entryId),
    [entryId, isMobile, theme]
  );
  const posterCandidates = useMemo(
    () => buildHowtoPosterCandidates(isMobile, theme, entryId),
    [entryId, isMobile, theme]
  );
  const webmCandidates = useMemo(
    () => buildHowtoWebmCandidates(isMobile, theme, entryId),
    [entryId, isMobile, theme]
  );

  const resetKey = `${entryId}:${isMobile ? "mobile" : "desktop"}:${theme}:${reduceMotion ? "still" : "play"}`;

  return (
    <figure
      className={cn(
        "overflow-hidden rounded-xl border border-border bg-muted/20 shadow-sm",
        className
      )}
    >
      {reduceMotion ? (
        <HowtoImgWithFallback
          key={`${resetKey}:poster`}
          candidates={posterCandidates}
          alt={localize(entry.alt)}
          priority={priority}
        />
      ) : (
        <HowtoPlayer
          key={resetKey}
          webmCandidates={webmCandidates}
          gifCandidates={gifCandidates}
          posterCandidates={posterCandidates}
          alt={localize(entry.alt)}
          priority={priority}
          labels={{
            fallback: localize("GIF fallback · loops automatically (pause not available)"),
            pause: localize("Pause"),
            play: localize("Play"),
            restart: localize("Restart"),
            speed: localize("Speed"),
            pauseRecording: localize("Pause recording"),
            playRecording: localize("Play recording"),
            restartRecording: localize("Restart recording"),
          }}
        />
      )}

      <figcaption className="px-3 py-2 text-xs text-muted-foreground">
        {localize(entry.title)}
      </figcaption>
    </figure>
  );
}

function HowtoPlayer({
  webmCandidates,
  gifCandidates,
  posterCandidates,
  alt,
  priority,
  labels,
}: {
  webmCandidates: string[];
  gifCandidates: string[];
  posterCandidates: string[];
  alt: string;
  priority: boolean;
  labels: {
    fallback: string;
    pause: string;
    play: string;
    restart: string;
    speed: string;
    pauseRecording: string;
    playRecording: string;
    restartRecording: string;
  };
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [webmIndex, setWebmIndex] = useState(0);
  const [useGif, setUseGif] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState<Speed>(0.75);

  const webmSrc = webmCandidates[Math.min(webmIndex, webmCandidates.length - 1)];
  const posterSrc = posterCandidates[0];

  useEffect(() => {
    const el = videoRef.current;
    if (!el || useGif) return;
    el.playbackRate = speed;
  }, [speed, useGif, webmSrc]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || useGif) return;
    if (playing) {
      void el.play().catch(() => setPlaying(false));
    } else {
      el.pause();
    }
  }, [playing, useGif, webmSrc]);

  // A <source> error does not bubble to <video>, so a stalled WebM can spin
  // forever. Fall back to the GIF if nothing is playable after a few seconds.
  useEffect(() => {
    if (useGif) return;
    const timeout = window.setTimeout(() => {
      const el = videoRef.current;
      if (!el || el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return;
      startTransition(() => setUseGif(true));
    }, 8000);
    return () => window.clearTimeout(timeout);
  }, [useGif, webmSrc]);

  if (useGif) {
    return (
      <div>
        <HowtoImgWithFallback candidates={gifCandidates} alt={alt} priority={priority} />
        <p className="border-t border-border/60 px-3 py-2 text-[11px] text-muted-foreground">
          {labels.fallback}
        </p>
      </div>
    );
  }

  return (
    <div>
      <video
        ref={videoRef}
        className="w-full h-auto bg-muted/30"
        autoPlay
        muted
        loop
        playsInline
        src={webmSrc}
        poster={posterSrc}
        aria-label={alt}
        onError={() => {
          if (webmIndex < webmCandidates.length - 1) {
            startTransition(() => setWebmIndex((i) => i + 1));
          } else {
            startTransition(() => setUseGif(true));
          }
        }}
      />
      <div className="flex flex-wrap items-center gap-2 border-t border-border/60 bg-muted/30 px-3 py-2">
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-muted"
          onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? labels.pauseRecording : labels.playRecording}
        >
          {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          {playing ? labels.pause : labels.play}
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium hover:bg-muted"
          onClick={() => {
            const el = videoRef.current;
            if (!el) return;
            el.currentTime = 0;
            setPlaying(true);
            void el.play().catch(() => {});
          }}
          aria-label={labels.restartRecording}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {labels.restart}
        </button>
        <div className="ml-auto flex items-center gap-1">
          <span className="text-[11px] text-muted-foreground">{labels.speed}</span>
          {([0.5, 0.75, 1] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={cn(
                "rounded-md px-2 py-1 text-xs font-medium",
                speed === s
                  ? "bg-accent text-accent-foreground"
                  : "border border-border bg-background hover:bg-muted"
              )}
              onClick={() => setSpeed(s)}
              aria-pressed={speed === s}
            >
              {s === 1 ? "1×" : `${s}×`}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function HowtoImgWithFallback({
  candidates,
  alt,
  priority,
}: {
  candidates: string[];
  alt: string;
  priority: boolean;
}) {
  const [index, setIndex] = useState(0);
  const src = candidates[Math.min(index, candidates.length - 1)];

  return (
    // eslint-disable-next-line @next/next/no-img-element -- GIF/poster paths; next/image does not animate GIFs reliably
    <img
      src={src}
      alt={alt}
      className="w-full h-auto"
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      onError={() => {
        if (index < candidates.length - 1) {
          startTransition(() => setIndex((i) => i + 1));
        }
      }}
    />
  );
}
